import { z } from 'zod';

/**
 * A posição da pessoa no ranking da edição — **por nota bruta**: quem acertou
 * mais vem antes. `score` é o número de acertos dela.
 */
export const StudentRankingSchema = z.object({
  position: z.number().int().positive(),
  totalParticipants: z.number().int().positive(),
  score: z.number().int().min(0),
  /** De quantas questões válidas o `score` é contado. */
  outOf: z.number().int().min(0),
});

export type StudentRanking = z.infer<typeof StudentRankingSchema>;

/**
 * Uma linha do cartão: a da pessoa, com nome e acertos, ou a de um vizinho,
 * que **só existe como forma** — mesmo desenho do `enamed-feedback-lp`, onde as
 * posições ao redor ficam borradas para preservar os outros participantes.
 */
export type RankingRow =
  | { kind: 'me'; position: number; name: string; score: number }
  | { kind: 'peer'; position: number };

/** Quantas linhas o cartão mostra: a pessoa e um vizinho de cada lado. */
export const RANKING_WINDOW_SIZE = 3;

/**
 * As posições do cartão, com a pessoa no meio quando dá. No topo e no fim da
 * lista a janela encolhe em vez de inventar vizinho que não existe. Mesma
 * regra do ENAMED.
 */
export function rankingWindow(
  position: number,
  total: number,
  size: number = RANKING_WINDOW_SIZE,
): number[] {
  if (total < 1 || size < 1) return [];
  const me = Math.min(Math.max(1, Math.trunc(position)), total);
  const neighbours = Math.floor((Math.trunc(size) - 1) / 2);
  const start = Math.max(1, me - neighbours);
  const end = Math.min(total, me + neighbours);
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

export function toRankingRows(
  ranking: StudentRanking,
  name: string,
): RankingRow[] {
  return rankingWindow(ranking.position, ranking.totalParticipants).map(
    (position): RankingRow =>
      position === ranking.position
        ? { kind: 'me', position, name, score: ranking.score }
        : { kind: 'peer', position },
  );
}

export function formatPosition(position: number): string {
  return `${position.toLocaleString('pt-BR')}º`;
}
